import type { Task } from '../tasks/types';
import type { TaskUpdate } from '../tasks/types';
import { localIsoDate } from '../myTasks/types';

/**
 * My Day, the arithmetic.
 *
 * Everything here is a pure function over the tasks and sections the page
 * already loaded, so the rules Kyu set can be read and tested without a
 * browser:
 *
 *   - two fixed lists, then the person's own, plus Unfinished, derived;
 *   - every open task is on the page, always;
 *   - newest activity on top, unless somebody dragged it somewhere, in which
 *     case it stays exactly where they put it;
 *   - the date in the header is a label, never a filter.
 *
 * docs/design-note-my-day.md has the argument for each, and
 * docs/design-note-my-day-sections.md for the custom ones.
 */

/**
 * The two lists everybody has. Slugs match the check constraint.
 *
 * There were three. Kyu removed Reminder / To Buy on 7 September — "there's no
 * use for a Reminder if it can't send a notification anyway" — and replaced it
 * with sections a person makes themselves.
 *
 * These two stay fixed, neither renamable nor deletable. To Do Today is not
 * only a name: it is the fallback every unresolved task lands in, so it has a
 * job beyond its label and should not be removable by somebody reading it as
 * just a label.
 *
 * The database still ACCEPTS the retired 'reminders' slug, deliberately.
 * Nothing writes it, and a task still carrying it is not stranded: it fails
 * `isBuiltIn` and falls through to the same fallback. Nobody loses a task.
 */
export type MyDayBuiltIn = 'priorities' | 'today';

export const MY_DAY_BUILT_INS: readonly { key: MyDayBuiltIn; label: string }[] = [
  { key: 'priorities', label: 'Top Priorities' },
  { key: 'today', label: 'To Do Today' },
];

/** Where anything unresolved goes. Referenced by name so the reason travels. */
export const MY_DAY_FALLBACK: MyDayBuiltIn = 'today';

export const MY_DAY_BUILT_IN_KEYS: readonly MyDayBuiltIn[] = MY_DAY_BUILT_INS.map(
  (section) => section.key,
);

export function isBuiltIn(value: string | null): value is MyDayBuiltIn {
  return value !== null && (MY_DAY_BUILT_IN_KEYS as readonly string[]).includes(value);
}

/** A row of public.my_day_sections, as the page reads it. */
export interface MyDaySectionRow {
  id: string;
  name: string;
  position: number;
  created_at: string;
}

/**
 * One column on the page. `key` is the built-in slug or the section's id, and
 * it is what the drop target, the React key and the board's list map all use,
 * so nothing downstream has to know which kind it is holding.
 */
export interface MyDayColumn {
  key: string;
  label: string;
  custom: boolean;
}

/**
 * Custom sections in reading order.
 *
 * position is not unique per user, on purpose — see the migration — so the
 * comparison falls through to created_at and then id. Two sections claiming
 * the same slot still have exactly one order, and it is the same order on
 * every device.
 */
export function sortSections(sections: readonly MyDaySectionRow[]): MyDaySectionRow[] {
  return [...sections].sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    if (a.created_at !== b.created_at) return a.created_at < b.created_at ? -1 : 1;
    return a.id < b.id ? -1 : 1;
  });
}

/**
 * The columns in reading order. A live TAP Console belongs ahead of the two
 * built-ins, but only its owner passes `liveHuddleSectionId`: a readable
 * section name is not proof that the current person owns Alex's link.
 */
export function columnsOf(
  sections: readonly MyDaySectionRow[],
  liveHuddleSectionId: string | null = null,
): MyDayColumn[] {
  const ordered = sortSections(sections);
  const huddle =
    liveHuddleSectionId === null
      ? undefined
      : ordered.find((section) => section.id === liveHuddleSectionId);
  return [
    ...(huddle === undefined ? [] : [{ key: huddle.id, label: huddle.name, custom: true }]),
    ...MY_DAY_BUILT_INS.map((section) => ({ ...section, custom: false })),
    ...ordered
      .filter((section) => section.id !== huddle?.id)
      .map((section) => ({
        key: section.id,
        label: section.name,
        custom: true,
      })),
  ];
}

/**
 * Which column a task belongs to, in three steps:
 *
 *   1. my_day_section_id set and that section still exists -> that section
 *   2. my_day_section is a built-in slug                   -> that built-in
 *   3. otherwise                                           -> To Do Today
 *
 * Step 3 is old, not new. It already caught unplaced tasks and the retired
 * 'reminders' slug, and the deleted-section case falls into it rather than
 * needing a catch of its own — the database sets my_day_section_id to null on
 * delete, and a null id is just another unresolved task.
 *
 * The `known` set is passed in rather than looked up so this stays pure and so
 * a stale id (deleted on another device, not yet refetched here) resolves the
 * same way a deleted one does instead of drawing a column that is not there.
 */
export function columnOf(task: Task, known: ReadonlySet<string>): string {
  const id = task.my_day_section_id;
  if (id !== null && known.has(id)) return id;
  return isBuiltIn(task.my_day_section) ? task.my_day_section : MY_DAY_FALLBACK;
}

/** The day this task counts as planned for. Falls back to the day it was made. */
export function plannedDay(task: Task): string {
  return task.my_day_planned_on ?? task.created_at.slice(0, 10);
}

/**
 * THE ROLLOVER. An open task in To Do Today planned for a day before today is
 * unfinished. Nothing moves it there: it is a reading of two dates, so a task
 * placed on Tuesday is in its list on Tuesday and under Unfinished on
 * Wednesday with no job running in between. Dragging it back into a list
 * plants today's date and it leaves Unfinished the same way.
 *
 * ONLY To Do Today rolls. Kyu, Sep 9, after a task he had put in his own
 * Queue section turned up under Unfinished the next morning: "Only the To Do
 * Today is the rolling, and anything that doesn't get done gets transferred
 * to Unfinished. But the other sections like Top Priorities and Queue or
 * whatever new section someone adds stay there, like a regular task list."
 *
 * The date still matters for the other lists — it is what "planned Sep 8"
 * reads from — it just no longer moves anything. `column` is the answer
 * columnOf gave for this task, passed in so this stays a pure reading.
 */
export function isUnfinished(task: Task, today: string, column: string): boolean {
  return column === MY_DAY_FALLBACK && !task.is_completed && plannedDay(task) < today;
}

/**
 * Completed today stays on the page, ticked, so the list shows what the day
 * produced and the header can say "3/5 complete". Completed before today is
 * gone from the lists — it is finished, and the page is about what is not —
 * but kept behind a fold so the record is still reachable.
 */
export function completedOn(task: Task): string | null {
  return task.completed_at === null ? null : localIsoDate(new Date(task.completed_at));
}

function byRecency(a: Task, b: Task): number {
  if (a.updated_at !== b.updated_at) return a.updated_at < b.updated_at ? 1 : -1;
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
  return a.id < b.id ? -1 : 1;
}

function bySlot(a: Task, b: Task): number {
  const pa = a.my_day_position ?? 0;
  const pb = b.my_day_position ?? 0;
  if (pa !== pb) return pa - pb;
  // Two tasks claiming the same slot: the one touched most recently is the one
  // that was just dropped there, and it should win the slot it was given.
  return byRecency(a, b);
}

/**
 * The merge. A slot is an index into the list. Tasks with a slot are placed at
 * it, in slot order; tasks without one fill the gaps, newest activity first.
 * Nothing is ever renumbered: a held task keeps its number until somebody
 * drags it again or releases it, and the flowing tasks arrange themselves
 * around whatever is held.
 *
 * A slot past the end of the list (the task above it was completed or moved)
 * simply lands at the end, still in slot order. That is the right answer for
 * "I put this fourth": there is no fourth any more, so it is last.
 */
export function arrangeList(tasks: readonly Task[]): Task[] {
  const held = tasks.filter((task) => task.my_day_position !== null).sort(bySlot);
  const flowing = tasks.filter((task) => task.my_day_position === null).sort(byRecency);
  const out: Task[] = [];
  let h = 0;
  let f = 0;
  while (h < held.length || f < flowing.length) {
    const next = held[h];
    if (next !== undefined && (next.my_day_position ?? 0) <= out.length) {
      out.push(next);
      h += 1;
    } else if (f < flowing.length) {
      out.push(flowing[f] as Task);
      f += 1;
    } else if (next !== undefined) {
      out.push(next);
      h += 1;
    }
  }
  return out;
}

export interface MyDayList {
  /** Open tasks in reading order, then today's completions beneath them. */
  tasks: Task[];
  /** How many of `tasks` are open. Drops land at or before this index. */
  openCount: number;
  /** Completed today. */
  doneCount: number;
}

export const EMPTY_LIST: MyDayList = { tasks: [], openCount: 0, doneCount: 0 };

export interface MyDayBoard {
  /** The columns to draw, in order. */
  columns: MyDayColumn[];
  /** Keyed by column key. Every column in `columns` has an entry. */
  lists: Record<string, MyDayList>;
  /** Open tasks whose planned day has passed. Not a drop target. */
  unfinished: Task[];
  /** Completed before today, newest completion first. */
  doneEarlier: Task[];
  /** Open tasks anywhere on the page. */
  openTotal: number;
  /** Completed today anywhere on the page. */
  doneTodayTotal: number;
}

function byCompletion(a: Task, b: Task): number {
  const ca = a.completed_at ?? '';
  const cb = b.completed_at ?? '';
  if (ca !== cb) return ca < cb ? 1 : -1;
  return byRecency(a, b);
}

/**
 * The whole page from one list of top-level tasks and the person's sections.
 * Subtasks are not passed in: they live inside their parent's panel, as they
 * do everywhere else.
 */
export function buildMyDay(
  tasks: readonly Task[],
  today: string,
  sections: readonly MyDaySectionRow[] = [],
  liveHuddleSectionId: string | null = null,
): MyDayBoard {
  const columns = columnsOf(sections, liveHuddleSectionId);
  const known = new Set(sections.map((section) => section.id));
  const open: Record<string, Task[]> = {};
  const doneToday: Record<string, Task[]> = {};
  for (const column of columns) {
    open[column.key] = [];
    doneToday[column.key] = [];
  }
  const unfinished: Task[] = [];
  const doneEarlier: Task[] = [];

  // Non-null assertions would be wrong here even though every key was seeded
  // above: columnOf can only return a built-in slug or a known section id, and
  // both are in `columns`, so the lookup is total. The `?? []` is belt for a
  // future edit that widens columnOf without widening the seed.
  for (const task of tasks) {
    const key = columnOf(task, known);
    if (task.is_completed) {
      if (completedOn(task) === today) {
        (doneToday[key] ?? []).push(task);
      } else {
        doneEarlier.push(task);
      }
    } else if (isUnfinished(task, today, key)) {
      unfinished.push(task);
    } else {
      (open[key] ?? []).push(task);
    }
  }

  const lists: Record<string, MyDayList> = {};
  for (const column of columns) {
    const arranged = arrangeList(open[column.key] ?? []);
    const done = (doneToday[column.key] ?? []).sort(byCompletion);
    lists[column.key] = {
      tasks: [...arranged, ...done],
      openCount: arranged.length,
      doneCount: done.length,
    };
  }

  // Unfinished flows by recency only. A slot was chosen inside a list on some
  // earlier day; it means nothing in a pile of carried-over work.
  unfinished.sort(byRecency);
  doneEarlier.sort(byCompletion);

  const count = (source: Record<string, Task[]>): number =>
    columns.reduce((n, column) => n + (source[column.key]?.length ?? 0), 0);

  return {
    columns,
    lists,
    unfinished,
    doneEarlier,
    openTotal: unfinished.length + count(open),
    doneTodayTotal: count(doneToday),
  };
}

/** Reads "3/5 complete" the way the header does. */
export function completionLabel(list: MyDayList): string {
  const total = list.openCount + list.doneCount;
  return total === 0 ? 'Nothing here yet' : `${list.doneCount}/${total} complete`;
}

/**
 * The write behind a drop. `index` is where the task should sit among the
 * OPEN tasks of the destination list — a drop on a row is that row's index, a
 * drop on the list itself is the end. Planting today's date is what lifts a
 * task out of Unfinished, and is harmless on one that was already here.
 *
 * BOTH SECTION COLUMNS ARE ALWAYS WRITTEN, one of them to null. A task is in
 * exactly one of two states, and setting the new one without clearing the old
 * would leave a row that says both, which the resolution order would then have
 * to arbitrate. Writing the pair keeps that situation from existing.
 *
 * Only ever sets a slot; it never renumbers neighbours. That is the point.
 */
export function placement(column: MyDayColumn, index: number, today: string): TaskUpdate {
  return {
    my_day_section: column.custom ? null : column.key,
    my_day_section_id: column.custom ? column.key : null,
    my_day_position: Math.max(0, Math.floor(index)),
    my_day_planned_on: today,
  };
}

/**
 * The way out of a held slot. Without this a list slowly freezes solid: every
 * task that was ever dragged stays pinned for good, and the recency rule has
 * nothing left to arrange.
 */
export const RELEASE: TaskUpdate = { my_day_position: null };

/**
 * Where a drop should land when the dragged row is already in this list: the
 * index it was dropped at, less one if it came from above (it is leaving that
 * spot, so everything beneath it shifts up by one).
 */
export function dropIndex(list: MyDayList, movingId: string, targetIndex: number): number {
  const from = list.tasks.findIndex((task) => task.id === movingId);
  const clamped = Math.min(Math.max(0, targetIndex), list.openCount);
  return from !== -1 && from < clamped ? clamped - 1 : clamped;
}

/** One my_day_position write: the moved task first, then any neighbour. */
export interface SlotWrite {
  id: string;
  position: number;
}

/** Newer than any real stamp, so a written row reads as just touched. */
const JUST_WRITTEN = '9999-12-31T23:59:59.999Z';

function drawnWith(tasks: readonly Task[], writes: readonly SlotWrite[]): string[] {
  const slot = new Map(writes.map((write) => [write.id, write.position]));
  return arrangeList(
    tasks.map((task) => {
      const position = slot.get(task.id);
      return position === undefined
        ? task
        : { ...task, my_day_position: position, updated_at: JUST_WRITTEN };
    }),
  ).map((task) => task.id);
}

/**
 * The writes behind Move up, Move down, Move to top and a drop (Kyu, 30 Sept:
 * "dragging up is hard"). `toIndex` is the FINAL index among the open rows,
 * so an in-list drop goes through dropIndex first. `incoming` is the task
 * when it is dropped in from another list or Unfinished.
 *
 * One write to the moved task is tried first, and is enough most of the time.
 * But arrangeList reads that slot against every other held and flowing row,
 * so one write alone often drew a different order from the one asked for
 * (hubtickets/kyu-20260930-myday-reorder, 194,923 of 433,344 moves). When it
 * does, the rows from the top down to the move are pinned at their new index,
 * the range grows while a lower row holds a slot inside it, and then grows one
 * row at a time until the order is the one asked for. Every row pinned means
 * unique slots 0..n-1, which arrangeList draws exactly, so this always ends.
 *
 * This changes "a drop only writes the dropped task" (placement, above): a
 * neighbour may now be pinned too, so that every move lands where aimed.
 * "Let it flow" still frees any of them. Completed rows are never written.
 */
export function reorderWrites(
  list: MyDayList,
  taskId: string,
  toIndex: number,
  incoming?: Task,
): SlotWrite[] {
  const open = list.tasks.slice(0, list.openCount);
  const from = open.findIndex((task) => task.id === taskId);
  const inserting = from === -1;
  if (inserting && incoming?.id !== taskId) return [];
  const to = Math.min(Math.max(0, Math.floor(toIndex)), inserting ? open.length : open.length - 1);
  if (!inserting && from === to) return [];

  const moved = [...open];
  const pool = inserting && incoming !== undefined ? [...open, incoming] : open;
  if (inserting && incoming !== undefined) {
    moved.splice(to, 0, incoming);
  } else {
    const [taken] = moved.splice(from, 1);
    if (taken === undefined) return [];
    moved.splice(to, 0, taken);
  }
  const asked = moved.map((task) => task.id).join(',');
  const draws = (writes: SlotWrite[]) => drawnWith(pool, writes).join(',') === asked;

  const single = [{ id: taskId, position: to }];
  if (draws(single)) return single;

  let last = inserting ? to : Math.max(from, to);
  for (let grew = true; grew;) {
    grew = false;
    moved.forEach((task, index) => {
      if (index > last && task.my_day_position !== null && task.my_day_position <= last) {
        last = index;
        grew = true;
      }
    });
  }
  const pinned = (upTo: number): SlotWrite[] => [
    { id: taskId, position: to },
    ...moved
      .slice(0, upTo + 1)
      .map((task, index) => ({ id: task.id, position: index }))
      .filter((write, index) => write.id !== taskId && moved[index]?.my_day_position !== index),
  ];
  let writes = pinned(last);
  while (last < moved.length - 1 && !draws(writes)) {
    last += 1;
    writes = pinned(last);
  }
  return writes;
}

/**
 * Room at the top for a task just added at slot 0 (Kyu, 7 Oct: "whenever I add
 * a task, it would always go on top. Always."). Every other held open row
 * moves down one slot, so slot 0 belongs to the new task alone. Without this
 * the new task SHARED slot 0 with the old top row, the tie went to whichever
 * was touched last, and a note on the old top row put it back above.
 *
 * Shifting every held row by one draws the same order with the new task in
 * front: arrangeList places held rows at their slot and lets flowing rows fill
 * the gaps, and one more row at index 0 moves every gap down by one too.
 * Written bottom row first. A write that fails half way then leaves the upper
 * rows where they were (a tie at worst, never two rows pushed past each
 * other). And each write stamps its row as just touched, so two rows that
 * already shared a slot keep their order: the one drawn higher is written
 * last, is the most recent, and still wins the tie.
 */
export function makeRoomAtTop(list: MyDayList, newTaskId: string): SlotWrite[] {
  return list.tasks
    .slice(0, list.openCount)
    .filter((task) => task.id !== newTaskId && task.my_day_position !== null)
    .map((task) => ({ id: task.id, position: (task.my_day_position ?? 0) + 1 }))
    .reverse();
}

/** A section name as it will be stored, or null if it is not one. */
export function cleanSectionName(raw: string): string | null {
  const name = raw.trim();
  return name.length >= 1 && name.length <= 40 ? name : null;
}

/** The position a new section takes: after everything the person already has. */
export function nextSectionPosition(sections: readonly MyDaySectionRow[]): number {
  return sections.reduce((max, section) => Math.max(max, section.position + 1), 0);
}

/**
 * Moving a section one place left or right.
 *
 * Returns every row whose position changed, which is two of them, or an empty
 * list at either end. Positions are rewritten from the reordered array rather
 * than swapped, so a set of rows that had drifted into duplicate or gapped
 * positions comes back clean instead of preserving the mess.
 */
export function reorderSections(
  sections: readonly MyDaySectionRow[],
  id: string,
  direction: -1 | 1,
): { id: string; position: number }[] {
  const ordered = sortSections(sections);
  const from = ordered.findIndex((section) => section.id === id);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= ordered.length) return [];
  const moved = [...ordered];
  const [taken] = moved.splice(from, 1);
  if (taken === undefined) return [];
  moved.splice(to, 0, taken);
  return moved
    .map((section, index) => ({ id: section.id, position: index }))
    .filter((row, index) => ordered[index]?.id !== row.id || ordered[index]?.position !== index);
}

export function todayIso(now: Date = new Date()): string {
  return localIsoDate(now);
}

/** "Saturday, 5 September" — the label the header carries. It is not a control. */
export function headerDate(now: Date = new Date()): string {
  return now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}
